import { useAuthContext } from "@features/auth/contexts/AuthContext";
import { useAppStore } from "@hooks/useAppStore";
import { useToast } from "@memorang/ui/components/Toast";
import { useAppTheme } from "@memorang/ui/useAppTheme";
import { useMutation } from "@tanstack/react-query";
import { useState } from "react";
import {
	type CmeCertificate,
	generateCmeCertificatePdfBase64,
	sendCmeCertificateByEmail,
} from "../../../api/hub/cme-service";
import { cmeCertificateInfo } from "../constants";
import type { CmeExportMethod, CmeType } from "../types";

type ExportParams = {
	fromDate: string;
	toDate: string;
	selectedMethod: CmeExportMethod;
	cmeType: CmeType;
	totalCreditsEarned: number;
};

export const useCmeCertificateExport = () => {
	const { showToast } = useToast();
	const {
		email,
		firstName: initialFirstName,
		lastName: initialLastName,
	} = useAuthContext();
	const companyName = useAppStore((state) => state.companyName);
	const theme = useAppTheme();

	const [firstName, setFirstName] = useState(initialFirstName ?? "");
	const [lastName, setLastName] = useState(initialLastName ?? "");

	const resetNamesToInitial = () => {
		setFirstName(initialFirstName ?? "");
		setLastName(initialLastName ?? "");
	};

	const emailMutation = useMutation({
		mutationFn: (payload: CmeCertificate) => sendCmeCertificateByEmail(payload),
		onSuccess: () => {
			showToast({
				message: "CME Certificate emailed successfully.",
				duration: 3000,
			});
		},
		onError: () => {
			showToast({
				message: "Failed to send CME Certificate. Please try again.",
				duration: 3000,
			});
		},
	});

	// PDF is generated server-side; persisting it to the device still needs a
	// file-download flow (no expo-file-system dependency in this app yet).
	const pdfMutation = useMutation({
		mutationFn: (payload: CmeCertificate) =>
			generateCmeCertificatePdfBase64(payload),
		onSuccess: () => {
			showToast({
				message: "Certificate generated successfully.",
				duration: 3000,
			});
		},
		onError: () => {
			showToast({
				message: "Failed to generate PDF. Please try again.",
				duration: 3000,
			});
		},
	});

	const handleExport = ({
		fromDate,
		toDate,
		selectedMethod,
		cmeType,
		totalCreditsEarned,
	}: ExportParams) => {
		const recipientName = `${firstName} ${lastName}`;

		if (!(fromDate && toDate)) {
			showToast({
				message: "Please select a date range to continue.",
				duration: 3000,
			});
			return;
		}

		if (!recipientName.trim()) {
			showToast({
				message: "Something went wrong. Please try again.",
				duration: 3000,
			});
			return;
		}

		if (!totalCreditsEarned) {
			showToast({
				message: "No credits earned within the selected date range.",
				duration: 3000,
			});
			return;
		}

		const payload: CmeCertificate = {
			recipientName,
			toEmail: email ?? "",
			companyName,
			startDate: fromDate,
			endDate: toDate,
			credits: totalCreditsEarned.toString(),
			primaryColor: theme.colors.secondary,
			...cmeCertificateInfo,
			infoText: `${cmeCertificateInfo.infoText}${cmeType === "physician" ? " for physicians" : ""}`,
		};

		if (selectedMethod === "email") {
			emailMutation.mutate(payload);
		} else {
			pdfMutation.mutate(payload);
		}
	};

	return {
		firstName,
		setFirstName,
		lastName,
		setLastName,
		resetNamesToInitial,
		handleExport,
		toEmail: email ?? "",
		isPending: emailMutation.isPending || pdfMutation.isPending,
	};
};
